import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MenuItem, Price } from '.';
import { Flipper, Flipped, spring } from 'react-flip-toolkit';
import { EMPTY_SECTION } from '../modules/constants';
import { SubSection } from './subsection';

// We only want to display animations if the "prefers-reduced-motion" CSS media feature is present and hasn't been set by the user.
// On older browsers this will be false. On newer browsers this will be true unless the user has explicitly opted out of motion
// in the OS preferences.
const motion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    (window.matchMedia('(prefers-reduced-motion: no-preference)') || {})
        .matches;
// Animate cards appearing and leaving simultaneously. Documented here:
// https://github.com/aholachek/react-flip-toolkit
function simultaneousAnimations({
    hideEnteringElements,
    animateEnteringElements,
    animateExitingElements,
    animateFlippedElements
}) {
    hideEnteringElements();
    animateExitingElements();
    animateFlippedElements();
    animateEnteringElements();
}

/**
 * Here is where we modify the spring config to specify the kind of animation we want.
 * This function deals with elements appearing.
 *
 * @function
 * @param    {HTMLElement} el    DOM element
 * @param    {number} index  index of the item to be animated in the animation list.
 */
function onAppear(el, index) {
    spring({
        onUpdate: (val) => {
            el.style.opacity = val; //eslint-disable-line
        },
        delay: 250 + index * 25
    });
}

/**
 * Here is where we modify the spring config to specify the kind of animation we want.
 * This function deals with elements disappearing.
 *
 * @function
 * @param    {HTMLElement} el    DOM element
 * @param    {number} index  index of the item to be animated in the animation list.
 * @param    {function} removeElement function passed in by the animation library that removes the element from the DOM
 * after animating.
 */

function onExit(el, index, removeElement) {
    spring({
        config: {
            overshootClamping: true
        },
        onUpdate: (val) => {
            el.style.transform = `scale(${1 - val})`; //eslint-disable-line
            el.style.opacity = 1 - val; //eslint-disable-line
        },
        onComplete: removeElement
    });
    return () => {
        removeElement();
    };
}

function NandinosBanner({ firstItem }) {
    return (
        <div className='nandinos'>
            <p>
                Set menu for kids under 10
                <br />
                1/ Choose your main
                <br />
                2/ Pick your flavour
                <br />
                3/ Pick two Nandino sides
                <br />
                4/ Choose a dessert OR drink
            </p>
            <Price {...firstItem} />
        </div>
    );
}

NandinosBanner.propTypes = {
    firstItem: PropTypes.object
};

/**
 * Display a menu section with header, conditionally with the ability to animate as per the users' "prefers-reduced-motion"
 * OS setting.
 *
 * @function
 * @param       {object} props Section props, containing an items array
 * @constructor
 */
function Section(props) {
    const {
        allergenFilter,
        bastes,
        className,
        dietFilter,
        displayName,
        hideCalories,
        id,
        idx,
        pdpInfo,
        items,
        setPdpInfo,
        nandinos,
        noPhotos,
        kind
    } = props;

    const fid = (id || '').replace(/:/g, '_');
    const [hasMounted, setHasMounted] = useState(false);

    const possibleSides = items.filter(
        (item) =>
            !item.modifiers.length && item.fulfilmentTypes.length && item.price
    );

    useEffect(() => {
        setHasMounted(true);
    }, []);
    function areWeTabbing(e) {
        if (e.which === 9) {
            document.documentElement.classList.remove('noFocusOutline');
            document.removeEventListener('keyup', areWeTabbing, true);
        }
    }
    useEffect(() => {
        if (hasMounted !== true || idx !== 1) {
            return;
        }

        document.addEventListener('keyup', areWeTabbing, true);
        return () => document.removeEventListener('keyup', areWeTabbing, false);
    });

    if (!items || !items.length) {
        return null;
    }

    /**
     * gets the props for an item and combines them with other props required to render the item.
     *
     * @function
     * @param    {object} item object representing props for a single menu item.
     * @returns  {object}      props required to display a single menu item.
     */
    function getMenuItemProps(item, kind, isNandinos) {
        return {
            ...item,
            allergenFilter,
            bastes,
            dietFilter,
            hideCalories,
            idx,
            pdpInfo,
            setPdpInfo,
            nandinos: isNandinos,
            possibleSides,
            noPhotos,
            sectionName: displayName,
            kind
        };
    }

    let subSection,
        restaurantGroup = null;

    return (
        <section className={className} id={fid} tabIndex={-1}>
            <h2>
                <em>{displayName}</em>
            </h2>
            {nandinos ? <NandinosBanner firstItem={items[0]} /> : null}
            {motion ? (
                // Motion, so render an animated list.
                <Flipper
                    flipKey={`${JSON.stringify(
                        allergenFilter
                    )}-${JSON.stringify(dietFilter)}-${JSON.stringify(
                        hideCalories
                    )}-${id}`}
                    spring='noWobble'
                    staggerConfig={{
                        forward: {
                            speed: 1
                        }
                    }}
                    handleEnterUpdateDelete={simultaneousAnimations}
                >
                    {items.map((item, iindex) => {
                        const menuItemProps = getMenuItemProps(
                            item,
                            kind,
                            nandinos
                        );
                        const itemRestaurantGroup =
                            item.restaurantGroup || null;
                        const itemSubSection = item.subsection || null;

                        const sectionChanged = itemSubSection !== subSection;

                        if (sectionChanged) {
                            restaurantGroup = null;
                        }

                        return (
                            <React.Fragment
                                key={item.plu + itemRestaurantGroup}
                            >
                                <SubSection
                                    itemSubSection={itemSubSection}
                                    lastSubSection={subSection}
                                    restaurantGroup={itemRestaurantGroup}
                                    lastRestaurantGroup={restaurantGroup}
                                >
                                    {itemSubSection
                                        ? (subSection = itemSubSection)
                                        : null}
                                    {itemRestaurantGroup
                                        ? (restaurantGroup =
                                              itemRestaurantGroup)
                                        : null}
                                </SubSection>
                                <Flipped
                                    flipId={item.id + itemRestaurantGroup}
                                    onAppear={onAppear}
                                    onExit={onExit}
                                    stagger
                                >
                                    {(flippedProps) => (
                                        <MenuItem
                                            {...menuItemProps}
                                            flippedProps={flippedProps}
                                            iindex={iindex}
                                        />
                                    )}
                                </Flipped>
                            </React.Fragment>
                        );
                    })}
                </Flipper>
            ) : (
                <div>
                    {items.map((item, idx) => {
                        const menuItemProps = getMenuItemProps(
                            item,
                            kind,
                            nandinos
                        );
                        const itemRestaurantGroup =
                            item.restaurantGroup || null;
                        const itemSubSection = item.subsection || null;

                        const sectionChanged = itemSubSection !== subSection;

                        if (sectionChanged) {
                            restaurantGroup = null;
                        }

                        return (
                            <React.Fragment
                                key={item.plu + itemRestaurantGroup}
                            >
                                <SubSection
                                    itemSubSection={itemSubSection}
                                    lastSubSection={subSection}
                                    restaurantGroup={itemRestaurantGroup}
                                    lastRestaurantGroup={restaurantGroup}
                                >
                                    {itemSubSection
                                        ? (subSection = itemSubSection)
                                        : null}
                                    {itemRestaurantGroup
                                        ? (restaurantGroup =
                                              itemRestaurantGroup)
                                        : null}
                                </SubSection>
                                <MenuItem key={idx} {...menuItemProps} />
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
            <p className='empty'>
                {EMPTY_SECTION} {displayName}.
            </p>
        </section>
    );
}

Section.propTypes = {
    allergenFilter: PropTypes.object,
    bastes: PropTypes.array,
    className: PropTypes.string,
    currentSection: PropTypes.string,
    dietFilter: PropTypes.array,
    displayName: PropTypes.string,
    hideCalories: PropTypes.bool,
    id: PropTypes.string,
    idx: PropTypes.number,
    pdpInfo: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    setCurrentSection: PropTypes.func,
    setPdpInfo: PropTypes.func,
    items: PropTypes.arrayOf(PropTypes.object),
    section: PropTypes.object,
    nandinos: PropTypes.bool,
    noPhotos: PropTypes.bool,
    kind: PropTypes.string
};

export { Section };
